import config from "appConfig";

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>A lot of different factors affect environmental noise (Type of source, distance between the source and the receiver, air and ground attenuation ...) that is why sometimes the same measurement, performed at several days of distance, leads to differences that can be as much as 10 dB. In that context, simulation and prediction of environmental noise is a real challenge. The trade-off between computational cost and accuracy of the predictions is thus the key question to choose the adequate modelling strategy. For practitioners, simplified models that can capture the big trends and tendancies will then be prefered to more sophisticated methods.
<h3 class=sectionHead>Problem of interest</h3>
<figure class=figure>
  <img src=${config.ASSETS_URL}/03/12/theory/figure-1.svg alt="Figure 1: Problem of interest.">
  <figcaption class=caption>Figure 1. Problem of interest.</figcaption>
</figure>
<p><a href=# data-img-src=${config.ASSETS_URL}/03/12/theory/figure-1.svg data-img-alt="Figure 1: Problem of interest.">Figure 1</a> presents the typical problem to be modelled. A source generates noise that propagates in open-air, a complex propagation medium. Noise can be diffracted by obstacles. The medium is semi-infinite absorbing a part of the acoustical energy. Similarly, a part of the energy is also absorbed by the ground. The objective of the simulation is to approach the sound pressure level at one or several receiving points. These levels can the visualised on soundmaps that can then be generated so as to have a representation of the sound levels around factory plants or highways... An example of soundmap is given in the principle part.<br class=newline>
<p>Sources can be point (equipment, plant) or line (roads, highways). Their noise level depends on several factors (density of the traffic, speed and type of vehicles, presence of reflecting surface in the neighborhood ...). In the absence of dissipation, the acoustic level decreases with the distance to the source (doubling the distance decreases the level of 6 dB for point sources and 3 dB for lineic ones). In real life, acoustic energy is also absorbed during its propagation, especially due to molecular relaxation mechanism. It depends on several factors (distance to the source, frequency content of noise, temperature, hydrometry, atmospheric pressure). As an example, at 4 kHz, for a temperature of 20 degrees and a relative humidity of 30%, the attenuation is around 50 dB by kilometer. Wind has also an influence for long distances (more than 50 m to the source). In addition, as speed of sound increases with altitude, it redirects the sound propagation path towards the ground. This creates a favorable propagation zone on the downwind side of the source and an unfavorable propagation zone on the upwind side of the source. For downwind measurements just a few dB of difference can be obtained compared to a non-wind situation, but for measurement against the wind, the shadow area can leads to a difference of more than 20 dB. Speed of sound is also influenced by temperature. For sunny days, sound goes up and leads to an unfavorable zone for its propagation. On clear nights, the temperature increases with altitude and noise is brought down to the ground. Ground has also an influence, indeed a part of the energy is reflected on it before reaching the receiver. This reflection, that create interferences with the direct propagation path, is associated to a partial absorption of the acoustic energy. This absorption depends on the properties of the ground and frequency. It is of the order of 5-10 dB but can grow up to 20 dB at some particular frequency (generally in a narrowband) for absorbing grounds. Propagation of sound is also influenced by obstacles, in particular acoustic barriers. Their efficiency depends two factors. The first one is frequency and the second one is the difference of path between the direct propagation (without barrier) and the one over the obstacle. The effect of a noise barrier depends on its height and the closer the barrier is close to the source, the more efficient it is.
<h3 class=sectionHead>Models and ISO standard</h3>
<p>Models for environmental noise propagation can also be classified in two categories (wave-based methods and ray methods). The most popular wave-based method is the Boundary-Element Method because it requires only a surfacic mesh (of the ground and obstacles in the present case) even if it is not well fitted to include gradients of temperature or wind effects. In addition, as propagation is on long distances, it requires a lot of degrees of freedom and thereby leads to tremendous computational cost. This cost should be compared to the uncertainties on the input parameters. Hence, the price for the accuracy of the method is too high to be used in practice. Ray methods are then preferred even if their approximations can be considered as very drastic. These methods offer the best compromise computational cost/precision.<br class=newline>
<p>
   A standard method (ISO 9613-2 Attenuation of sound during propagation outdoors), based on ray tracing, is often used to predict attenuation of sound and pressure levels in an environmental noise context. The range of application of this method are mainly industrial and traffic noise. It cannot be applied to aircraft noise or explosion noise (military activities). It determines an equivalent continuous A-weighted sound pressure level, which is an average of a namely short-term downwind anq long-term overall sound pressure level. Long-term contribution encompasses levels for a wide variety of meteorological conditions. The different steps of there methods are first to decompose each source as a point ones. Extended sources are decomposed in a set of sections, each having a certain sound power and directivity. Then, the method computes the paths between each source and the receiving point. For each one of these paths, the sound pressure
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>L</mi>
   </math>
   is estimated for the eight standardised octave bands as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>L</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>D</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mi>A</mi>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   is the sound power level. It is a combination of the level of the sources and also takes into account flux of vehicules in the case of traffic noise.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   is a directivity correction that represents the deviation of the source directivity from the level of an omnidirectional point source.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
   </math>
   is the attenuation. The key point of the method is the estimation of this attenuation for each acoustic path. It is written as a simple sum that mainly depends on the length
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
   </math>
   of the path
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>A</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
                  <mi>i</mi>
                  <mi>v</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
                  <mi>t</mi>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>g</mi>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
                  <mi>a</mi>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
                  <mi>i</mi>
                  <mi>s</mi>
                  <mi>c</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(2)
</table>
<ul class=itemize1>
   <li class=itemize>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>A</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
               <mi>i</mi>
               <mi>v</mi>
            </mrow>
         </msub>
      </math>
      is the attenuation due to geometric divergence. It is equal to
      <table class=equation>
         <tr>
            <td style="font-size: 11px">
               <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                  <msub>
                     <mrow>
                        <mi>A</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                        <mi>i</mi>
                        <mi>v</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>=</mo>
                  <mn>2</mn>
                  <mn>0</mn>
                  <mi class=qopname>log</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi>d</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <mn>1</mn>
                  <mn>1</mn>
                        <msub>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>=</mo>
                  <mn>1</mn>
                  <mstyle class=text>
                     <mtext class=textrm mathvariant=normal> m</mtext>
                  </mstyle>
                     </math>
            <td class=eq-no>(3)
      </table>
      <p>This corresponds to the attenuation of a spherical wave in free field.
   <li class=itemize>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>A</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>t</mi>
               <mi>m</mi>
            </mrow>
         </msub>
      </math>
      is the attenuation due to the atmosphere. A simplified expression is
      <table class=equation>
         <tr>
            <td>
               <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                  <msub>
                     <mrow>
                        <mi>A</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                        <mi>t</mi>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>=</mo>
                  <mfrac>
                     <mrow>
                        <mi>α</mi>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                        <mn>0</mn>
                     </mrow>
                  </mfrac>
                     </math>
            <td class=eq-no>(4)
      </table>
      <p>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>α</mi>
         </math>
         is a parameter, given by the norm that depends on meteorological conditions and frequency. The range of variation of this parameter is on 3 orders of magnitudes (from 0.1 to values to close to 100).
   <li class=itemize>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>A</mi>
            </mrow>
            <mrow>
               <mi>g</mi>
               <mi>r</mi>
            </mrow>
         </msub>
      </math>
      is the attenuation due to the ground effect which interferes with the sound propagating directly from source to receiver. Its estimation is simple to calculate (but not straightforward as composed of the attenuation in three regions depending on the distance to the source)
   <li class=itemize>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>A</mi>
            </mrow>
            <mrow>
               <mi>b</mi>
               <mi>a</mi>
               <mi>r</mi>
            </mrow>
         </msub>
      </math>
      is associated to the attenuation by barriers. All objects cannot be considered as barriers and the conditions are given by the norm. The methods to compute this attenuation are obstacle-dependent and include diffraction effects when they cannot be neglected.
   <li class=itemize>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>A</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
               <mi>i</mi>
               <mi>s</mi>
               <mi>c</mi>
            </mrow>
         </msub>
      </math>
      corresponds to miscellaneous effects. It can be foliage, scattering by industrial sites or housings.
</ul>
<p>This method provides a rough estimate of the sound pressure level for a relatively small computational cost but at the price of several approximations that should be kept in mind (no interference phenomena, limited description of sources, ground meteorological effects). For all that reasons, one can estimate that it is a method which is well adapted to long range propagation in sparse building and industrial environment.`;

export default theory;
